import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { residentialComplexApi, type SearchFilters } from '../api/residentialComplexApi'
import type { ResidentialComplex } from '@/types/entities/residential-complex'

/**
 * Store для Residential Complexes feature
 * Управляет списком ЖК, текущим ЖК и пагинацией
 */
export const useResidentialComplexStore = defineStore('residentialComplex', () => {
  // State
  const complexes = ref<ResidentialComplex[]>([])
  const currentComplex = ref<ResidentialComplex | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  
  // Pagination
  const currentPage = ref(1)
  const perPage = ref(12)
  const total = ref(0)
  const totalPages = ref(0)

  // Computed
  const hasNextPage = computed(() => currentPage.value < totalPages.value)
  const hasPrevPage = computed(() => currentPage.value > 1)

  /**
   * Поиск ЖК с фильтрами
   */
  async function search(filters: SearchFilters = {}, page = 1) {
    loading.value = true
    error.value = null

    try {
      const response = await residentialComplexApi.search(filters, page, perPage.value)
      
      complexes.value = response.residentialComplexes
      currentPage.value = response.pagination.page
      total.value = response.pagination.total
      totalPages.value = response.pagination.totalPages
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to search complexes'
      console.error('Failed to search complexes:', e)
    } finally {
      loading.value = false
    }
  }

  /**
   * Загрузить ЖК по слагу города и слагу
   */
  // DEV-2371: адрес последнего запроса. Ответ на более ранний, пришедший
  // позже (быстрый переход между ЖК), выбрасываем: иначе на экране оказался
  // бы прежний ЖК, а страница по его слагу увела бы на его адрес.
  let lastBySlugRequest = ''

  async function loadBySlug(citySlug: string, slug: string) {
    const request = `${citySlug}/${slug}`
    lastBySlugRequest = request
    loading.value = true
    error.value = null
    currentComplex.value = null

    try {
      const complex = await residentialComplexApi.getBySlug(citySlug, slug)
      if (request === lastBySlugRequest) {
        currentComplex.value = complex
      }
    } catch (e) {
      if (request === lastBySlugRequest) {
        error.value = e instanceof Error ? e.message : 'Failed to load complex'
      }
      console.error('Failed to load complex by slug:', e)
    } finally {
      if (request === lastBySlugRequest) {
        loading.value = false
      }
    }
  }

  /**
   * Следующая страница
   */
  async function nextPage(filters: SearchFilters = {}) {
    if (hasNextPage.value) {
      await search(filters, currentPage.value + 1)
    }
  }

  /**
   * Предыдущая страница
   */
  async function prevPage(filters: SearchFilters = {}) {
    if (hasPrevPage.value) {
      await search(filters, currentPage.value - 1)
    }
  }

  /**
   * Перейти на страницу
   */
  async function goToPage(page: number, filters: SearchFilters = {}) {
    if (page >= 1 && page <= totalPages.value) {
      await search(filters, page)
    }
  }

  /**
   * Сбросить состояние
   */
  function reset() {
    complexes.value = []
    currentComplex.value = null
    loading.value = false
    error.value = null
    currentPage.value = 1
    total.value = 0
    totalPages.value = 0
  }

  return {
    // State
    complexes,
    currentComplex,
    loading,
    error,
    currentPage,
    perPage,
    total,
    totalPages,

    // Computed
    hasNextPage,
    hasPrevPage,

    // Actions
    search,
    loadBySlug,
    nextPage,
    prevPage,
    goToPage,
    reset,
  }
})
